<script setup lang="ts">
import { watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { useUmsokn } from '@/composables/Applications/UseUmsokn'
import DatePicker from '../../Shared/DatePicker.vue'

const { t } = useI18n()
const { umsokn } = useUmsokn()

const props = defineProps<{
  disable?: boolean
}>()

// Ensure the nested object exists
if (umsokn.value && !umsokn.value.takeOverLoanInformation) {
  umsokn.value.takeOverLoanInformation = {
    loanNumber: '',
    issueDate: '',
    initialAmount: ''
  }
}

// Validation rules
const rules = {
  umsokn: {
    takeOverLoanInformation: {
      loanNumber: { required },
      issueDate: { required },
      initialAmount: { required }
    }
  }
}

const v$ = useVuelidate(rules, { umsokn })

// Format amount with thousand separators
watch(
  () => umsokn.value?.takeOverLoanInformation?.initialAmount,
  (newVal) => {
    if (newVal != null) {
      const result = newVal
        .toString()
        .replace(/\D/g, '')
        .replace(/\B(?=(\d{3})+(?!\d))/g, '.')
      umsokn.value!.takeOverLoanInformation.initialAmount = result
    }
  }
)

defineExpose({
  validate() {
    v$.value.$touch()
    return !v$.value.$invalid
  }
})
</script>

<template>
  <div v-if="umsokn">
    <v-col>
      <div class="forms__header">
        {{ t('lan_nafnabeyting_umsokn_step5_header') }}
      </div>

      <div class="list-item">
        <v-row>
          <v-col md="4" cols="12">
            <v-text-field
              v-model="umsokn.takeOverLoanInformation.loanNumber"
              :readonly="props.disable"
              :label="t('lan_nafnabeyting_umsokn_step5_lansnumer')"
              variant="outlined"
              density="comfortable"
              :error="v$.umsokn.takeOverLoanInformation.loanNumber.$invalid"
              :error-messages="
                v$.umsokn.takeOverLoanInformation.loanNumber.$error
                  ? [t('umsokn_general_required_error')]
                  : []
              "
            />
          </v-col>

          <v-col md="4" cols="12">
            <DatePicker
              v-model="umsokn.takeOverLoanInformation.issueDate"
              :label="t('lan_nafnabeyting_umsokn_step5_utgafudagur')"
              :disabled="props.disable"
              :error="v$.umsokn.takeOverLoanInformation.issueDate.$error"
              :error-messages="
                v$.umsokn.takeOverLoanInformation.issueDate.$error
                  ? [t('umsokn_general_required_error')]
                  : []
              "
            />
          </v-col>

          <v-col md="4" cols="12">
            <v-text-field
              v-model="umsokn.takeOverLoanInformation.initialAmount"
              :readonly="props.disable"
              :label="t('lan_nafnabeyting_umsokn_step5_fjarhaed')"
              variant="outlined"
              density="comfortable"
              :error="v$.umsokn.takeOverLoanInformation.initialAmount.$invalid"
              :error-messages="
                v$.umsokn.takeOverLoanInformation.initialAmount.$error
                  ? [t('umsokn_general_required_error')]
                  : []
              "
            />
          </v-col>
        </v-row>
      </div>
    </v-col>
  </div>
</template>

<style scoped lang="scss">
.list-item {
  margin-top: 16px;
}
</style>
